/**
 * MULTIPLAYER (v9) — group play over the product's PROVEN family-fun
 * WebSocket relay.
 * ============================================================================
 * Backend: services-python/family-fun-service/src/api/routes/websocket_session.py
 * (NETWORK-MP). Endpoint:
 *
 *     wss://<host>/ws/family-fun/sessions/<CODE>
 *         ?guest=1&participant_id=<uuid>&name=<pseudo>&emoji=🕊️   (no JWT)
 *         ?token=<jwt>&user_id=<uuid>&name=<pseudo>               (authed)
 *
 * Facts proven by reading the relay source (no REST call needed):
 *   - `session_manager.connect()` AUTO-CREATES the shared roster on the first
 *     WS connect (`rss.ensure_session`), so joining a fresh CODE just works.
 *   - The GUEST path (`F-GAMES-GUEST-MODE`) accepts any non-empty
 *     participant_id verbatim — no JWT required, perfect for the standalone
 *     Kingdom Quest preview.
 *   - Every frame is JSON `{type, payload}`. Frame types the relay does not
 *     reserve are broadcast VERBATIM to all peers with a server-stamped
 *     `_from` (sender excluded), which is exactly the extension point the
 *     web/mobile games already use (`action` frames — see
 *     useCoopQuestSession.ts / useNetworkSession.ts).
 *   - Session codes must be 3-12 alphanumeric chars (BE validation).
 *
 * Kingdom Quest protocol (namespaced `action` frames, `payload.g === "kq"`):
 *   {g:"kq", kind:"hello",    s:<sceneId>, n:<name>}   join/scene-change/us-reply
 *   {g:"kq", kind:"pos",      s:<sceneId>, x, z, ry}   ~10 Hz self position
 *   {g:"kq", kind:"progress", s:<sceneId>, id:<iid>}   objective advance
 *   {g:"kq", kind:"bye"}                               best-effort leave
 *
 * Fail-open by design: no network / auth refusal / max retries → the game
 * silently continues SOLO (never throws into the engine loop).
 *
 * FALLBACK RELAY (clearly marked, dev-only): `?mprelay=local` swaps the WS
 * transport for a BroadcastChannel bridge (same frame semantics, same-machine
 * tabs only) so the multiplayer engine itself can be proven offline. The
 * DEFAULT is always the real product relay.
 */

export interface PeerInfo {
  id: string;
  name: string;
  /** SceneDef.id the peer last reported (null until a hello/pos arrives). */
  sceneId: string | null;
  x: number;
  z: number;
  ry: number;
  /** True once at least one pos frame arrived (mesh can be spawned). */
  hasPos: boolean;
  lastSeen: number;
}

export interface MultiplayerOpts {
  /** Session code (3-12 alphanumerics — relay validation). */
  code: string;
  /** Display name shown above the avatar. */
  name: string;
  /** Relay host, default dev.kconnection.org (ws:// for localhost). */
  host?: string;
  /** Optional JWT — when present the authed path is used instead of guest. */
  token?: string;
  /** "ws" (default, real product relay) | "local" (BroadcastChannel FALLBACK). */
  relay?: "ws" | "local";
}

interface Frame {
  type: string;
  payload?: unknown;
  _from?: string;
}

const POS_INTERVAL_MS = 100; // ~10 Hz
const POS_HEARTBEAT_MS = 2000; // resend even when idle (late joiners)
const PING_INTERVAL_MS = 25_000; // matches FamilyFunWS keepalive
const PEER_STALE_MS = 90_000;
const SWEEP_INTERVAL_MS = 10_000;
const MAX_RECONNECT_ATTEMPTS = 12;
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 8_000;

/** Distinct, readable avatar colors — picked by stable hash of the peer id. */
const PEER_COLORS = [
  "#d1495b", "#3f8f4a", "#e0a83a", "#7a5ab8", "#3a8fb8",
  "#c86a9a", "#5ab88a", "#b8703a", "#6a7ac8", "#8fa83a",
];

export function peerColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PEER_COLORS[h % PEER_COLORS.length];
}

export function genSessionCode(): string {
  const A = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // unambiguous alphabet
  let s = "";
  for (let i = 0; i < 6; i++) s += A[Math.floor(Math.random() * A.length)];
  return s;
}

/* ────────────────────────────────────────────────────────────────────────── */

interface RelayTransport {
  open(): void;
  close(): void;
  /** Returns false when the frame could not be sent (not connected). */
  send(type: string, payload: unknown): boolean;
  readonly connected: boolean;
  onFrame: (f: Frame) => void;
  onOpen: () => void;
  onClose: () => void;
}

/** REAL product relay — native WebSocket, auto-reconnect w/ backoff, fail-open. */
class WsRelayTransport implements RelayTransport {
  onFrame: (f: Frame) => void = () => {};
  onOpen: () => void = () => {};
  onClose: () => void = () => {};

  private ws: WebSocket | null = null;
  private closed = false;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly url: string) {}

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  open(): void {
    this.closed = false;
    this.dial();
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopPing();
    try {
      this.ws?.close(1000, "Client closed");
    } catch { /* fail-open */ }
    this.ws = null;
  }

  send(type: string, payload: unknown): boolean {
    if (!this.connected) return false;
    try {
      this.ws!.send(JSON.stringify({ type, payload }));
      return true;
    } catch {
      return false;
    }
  }

  private dial(): void {
    if (this.closed) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.startPing();
      this.onOpen();
    };
    ws.onmessage = (ev) => {
      try {
        const f = JSON.parse(String(ev.data)) as Frame;
        if (f && typeof f.type === "string") this.onFrame(f);
      } catch { /* drop malformed */ }
    };
    ws.onclose = (ev) => {
      this.stopPing();
      this.onClose();
      if (this.closed) return;
      // 4001 auth / 4400 bad code / 4403 kicked → terminal (solo fail-open).
      if (ev.code === 4001 || ev.code === 4400 || ev.code === 4403) {
        console.warn(`[KQ-MP] relay refused connection (code ${ev.code} ${ev.reason || ""}) — continuing solo`);
        this.closed = true;
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => { /* onclose follows; fail-open */ };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    if (this.attempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn("[KQ-MP] max reconnect attempts reached — continuing solo");
      this.closed = true;
      return;
    }
    this.attempts += 1;
    const base = Math.min(
      BASE_RECONNECT_DELAY_MS * 2 ** (this.attempts - 1),
      MAX_RECONNECT_DELAY_MS,
    );
    const delay = base + Math.random() * base * 0.3;
    this.reconnectTimer = setTimeout(() => this.dial(), delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => this.send("ping", {}), PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }
}

/**
 * ⚠️ LOCAL FALLBACK RELAY — BroadcastChannel bridge (same machine, same
 * browser only). NOT the product path; used only via `?mprelay=local` to
 * prove the multiplayer engine when the network relay is unreachable.
 * The sender stamps `_from` itself (there is no server here).
 */
class LocalRelayTransport implements RelayTransport {
  onFrame: (f: Frame) => void = () => {};
  onOpen: () => void = () => {};
  onClose: () => void = () => {};

  private ch: BroadcastChannel | null = null;

  constructor(
    private readonly code: string,
    private readonly selfId: string,
  ) {}

  get connected(): boolean {
    return this.ch !== null;
  }

  open(): void {
    if (this.ch) return;
    this.ch = new BroadcastChannel(`kq-mp-${this.code}`);
    this.ch.onmessage = (ev) => {
      const f = ev.data as Frame;
      if (f && typeof f.type === "string" && f._from !== this.selfId) this.onFrame(f);
    };
    console.warn("[KQ-MP] LOCAL FALLBACK RELAY active (BroadcastChannel) — same-machine tabs only, NOT the product relay");
    setTimeout(() => this.onOpen(), 0);
  }

  close(): void {
    this.ch?.close();
    this.ch = null;
    this.onClose();
  }

  send(type: string, payload: unknown): boolean {
    if (!this.ch) return false;
    try {
      this.ch.postMessage({ type, payload, _from: this.selfId } satisfies Frame);
      return true;
    } catch {
      return false;
    }
  }
}

/* ────────────────────────────────────────────────────────────────────────── */

/** Payload namespace guard for Kingdom Quest frames. */
interface KqPayload {
  g: "kq";
  kind: "hello" | "pos" | "progress" | "bye";
  s?: string;
  n?: string;
  x?: number;
  z?: number;
  ry?: number;
  id?: string;
}

function isKqPayload(p: unknown): p is KqPayload {
  return !!p && typeof p === "object" && (p as { g?: unknown }).g === "kq"
    && typeof (p as { kind?: unknown }).kind === "string";
}

export class MultiplayerSystem {
  readonly code: string;
  readonly selfName: string;
  readonly relayKind: "ws" | "local";

  /** Engine hooks (single-slot; WorldManager re-wires per scene build). */
  onStatus: ((connected: boolean, peerCount: number) => void) | null = null;
  onPeerUpsert: ((p: PeerInfo) => void) | null = null;
  onPeerLeft: ((id: string) => void) | null = null;
  onRemoteProgress: ((interactableId: string, fromId: string, sceneId: string) => void) | null = null;

  private readonly transport: RelayTransport;
  private readonly selfId: string;
  private peers = new Map<string, PeerInfo>();
  private sceneId: string | null = null;
  private lastPosSentAt = 0;
  private lastPos = { x: NaN, z: NaN, ry: NaN };
  private sweepTimer: ReturnType<typeof setInterval> | null = null;
  private helloRepliedAt = 0;
  private disposed = false;

  constructor(opts: MultiplayerOpts) {
    this.code = opts.code.toUpperCase();
    this.selfName = (opts.name || "Pèlerin").slice(0, 24);
    this.relayKind = opts.relay === "local" ? "local" : "ws";

    // Stable per-tab identity: the relay roster keys by this id; sessionStorage
    // keeps it across refresh (same slot reconnect — matches the guest-mode
    // pattern of the product FE).
    let pid = "";
    try {
      pid = sessionStorage.getItem("kq_mp_pid") || "";
      if (!pid) {
        pid = typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `kq-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
        sessionStorage.setItem("kq_mp_pid", pid);
      }
    } catch {
      pid = `kq-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    }
    this.selfId = pid;

    if (this.relayKind === "local") {
      this.transport = new LocalRelayTransport(this.code, this.selfId);
    } else {
      const host = (opts.host || "dev.kconnection.org").replace(/\/+$/, "");
      const insecure = /^(localhost|127\.|0\.0\.0\.0|\[::1\])/.test(host);
      const proto = insecure ? "ws:" : "wss:";
      const qp = new URLSearchParams();
      if (opts.token) {
        qp.set("token", opts.token);
        qp.set("user_id", this.selfId);
        qp.set("name", this.selfName);
      } else {
        // F-GAMES-GUEST-MODE — no JWT required by the relay.
        qp.set("guest", "1");
        qp.set("participant_id", this.selfId);
        qp.set("name", this.selfName);
        qp.set("emoji", "🕊️");
      }
      const url = `${proto}//${host}/ws/family-fun/sessions/${encodeURIComponent(this.code)}?${qp.toString()}`;
      this.transport = new WsRelayTransport(url);
    }

    this.transport.onOpen = () => {
      this.sendHello();
      // Re-announce position promptly so peers place us after (re)connect.
      this.lastPosSentAt = 0;
      this.emitStatus();
    };
    this.transport.onClose = () => this.emitStatus();
    this.transport.onFrame = (f) => this.handleFrame(f);
  }

  /* ── lifecycle ─────────────────────────────────────────────────────── */

  connect(): void {
    if (this.disposed) return;
    this.transport.open();
    this.sweepTimer = setInterval(() => this.sweepStale(), SWEEP_INTERVAL_MS);
    // Best-effort goodbye so peers drop our avatar immediately on tab close.
    window.addEventListener("beforeunload", this.onBeforeUnload);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.transport.send("action", { g: "kq", kind: "bye" } satisfies KqPayload);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    if (this.sweepTimer) clearInterval(this.sweepTimer);
    this.sweepTimer = null;
    this.transport.close();
    this.peers.clear();
  }

  private onBeforeUnload = (): void => {
    this.transport.send("action", { g: "kq", kind: "bye" } satisfies KqPayload);
  };

  /* ── engine-facing API ─────────────────────────────────────────────── */

  get connected(): boolean {
    return this.transport.connected;
  }

  get id(): string {
    return this.selfId;
  }

  /** Peers currently known on the session (any scene of the parcours). */
  get peerCount(): number {
    return this.peers.size;
  }

  /** Display name of a known peer ("" when unknown). */
  peerName(id: string): string {
    return this.peers.get(id)?.name || "";
  }

  /** Peers reported in the given scene, with a live position. */
  peersInScene(sceneId: string): PeerInfo[] {
    const out: PeerInfo[] = [];
    for (const p of this.peers.values()) {
      if (p.hasPos && p.sceneId === sceneId) out.push(p);
    }
    return out;
  }

  /** WorldManager calls this on scene build/teardown (null = leaving). */
  setScene(sceneId: string | null): void {
    this.sceneId = sceneId;
    if (sceneId) this.sendHello();
  }

  /** Throttled (~10 Hz + idle heartbeat) self-position broadcast. */
  sendPos(x: number, z: number, ry: number): void {
    if (!this.sceneId) return;
    const now = Date.now();
    if (now - this.lastPosSentAt < POS_INTERVAL_MS) return;
    const moved =
      Math.abs(x - this.lastPos.x) > 0.02 ||
      Math.abs(z - this.lastPos.z) > 0.02 ||
      Math.abs(ry - this.lastPos.ry) > 0.02;
    if (!moved && now - this.lastPosSentAt < POS_HEARTBEAT_MS) return;
    this.lastPosSentAt = now;
    this.lastPos = { x, z, ry };
    this.transport.send("action", {
      g: "kq", kind: "pos", s: this.sceneId,
      x: round2(x), z: round2(z), ry: round2(ry),
    } satisfies KqPayload);
  }

  /** Broadcast an objective advance (deliver/collect/lever/guide id). */
  sendProgress(interactableId: string): void {
    if (!this.sceneId) return;
    this.transport.send("action", {
      g: "kq", kind: "progress", s: this.sceneId, id: interactableId,
    } satisfies KqPayload);
  }

  /** Test/debug introspection (used by the 2-window Playwright proof). */
  debugSnapshot(): {
    selfId: string; code: string; relay: string; connected: boolean;
    scene: string | null;
    self: { x: number; z: number; ry: number };
    peers: Array<{ id: string; name: string; scene: string | null; x: number; z: number; ry: number; hasPos: boolean }>;
  } {
    return {
      selfId: this.selfId,
      code: this.code,
      relay: this.relayKind,
      connected: this.connected,
      scene: this.sceneId,
      self: { x: this.lastPos.x, z: this.lastPos.z, ry: this.lastPos.ry },
      peers: [...this.peers.values()].map((p) => ({
        id: p.id, name: p.name, scene: p.sceneId, x: p.x, z: p.z, ry: p.ry, hasPos: p.hasPos,
      })),
    };
  }

  /* ── inbound ───────────────────────────────────────────────────────── */

  private handleFrame(f: Frame): void {
    switch (f.type) {
      case "session_snapshot": {
        const snap = f.payload as { players?: Array<{ id?: string; name?: string }> } | null;
        for (const row of snap?.players ?? []) {
          if (!row?.id || row.id === this.selfId) continue;
          this.upsertPeer(row.id, { name: row.name || undefined });
        }
        this.emitStatus();
        return;
      }
      case "player_joined": {
        const p = f.payload as { id?: string; name?: string } | null;
        if (p?.id && p.id !== this.selfId) {
          this.upsertPeer(p.id, { name: p.name || undefined });
          // A newcomer needs our position/name right away.
          this.sendHello();
          this.lastPosSentAt = 0;
          this.emitStatus();
        }
        return;
      }
      case "player_left": {
        const p = f.payload as { id?: string } | null;
        if (p?.id) this.dropPeer(p.id);
        return;
      }
      case "action": {
        const from = f._from;
        if (!from || from === this.selfId) return;
        const pl = f.payload;
        if (!isKqPayload(pl)) return; // foreign game frame on a shared code
        this.handleKq(from, pl);
        return;
      }
      default:
        return; // pong / foreign frames — ignore
    }
  }

  private handleKq(from: string, p: KqPayload): void {
    switch (p.kind) {
      case "hello": {
        this.upsertPeer(from, { name: p.n, sceneId: p.s ?? null });
        // Answer (throttled) so the newcomer learns OUR name/scene too, then
        // re-announce position promptly.
        const now = Date.now();
        if (now - this.helloRepliedAt > 2000) {
          this.helloRepliedAt = now;
          this.sendHelloRaw();
          this.lastPosSentAt = 0;
        }
        this.emitStatus();
        return;
      }
      case "pos": {
        if (typeof p.x !== "number" || typeof p.z !== "number") return;
        this.upsertPeer(from, {
          sceneId: p.s ?? null,
          x: p.x, z: p.z, ry: typeof p.ry === "number" ? p.ry : 0,
          hasPos: true,
        });
        return;
      }
      case "progress": {
        if (typeof p.id === "string" && p.id && p.s) {
          this.upsertPeer(from, { sceneId: p.s });
          this.onRemoteProgress?.(p.id, from, p.s);
        }
        return;
      }
      case "bye":
        this.dropPeer(from);
        return;
    }
  }

  /* ── internals ─────────────────────────────────────────────────────── */

  private sendHello(): void {
    this.sendHelloRaw();
  }

  private sendHelloRaw(): void {
    this.transport.send("action", {
      g: "kq", kind: "hello", s: this.sceneId ?? undefined, n: this.selfName,
    } satisfies KqPayload);
  }

  private upsertPeer(
    id: string,
    patch: Partial<Pick<PeerInfo, "name" | "sceneId" | "x" | "z" | "ry" | "hasPos">>,
  ): void {
    let p = this.peers.get(id);
    const isNew = !p;
    if (!p) {
      p = { id, name: "", sceneId: null, x: 0, z: 0, ry: 0, hasPos: false, lastSeen: 0 };
      this.peers.set(id, p);
    }
    if (patch.name !== undefined && patch.name) p.name = patch.name.slice(0, 24);
    if (patch.sceneId !== undefined) p.sceneId = patch.sceneId;
    if (patch.x !== undefined) p.x = patch.x;
    if (patch.z !== undefined) p.z = patch.z;
    if (patch.ry !== undefined) p.ry = patch.ry;
    if (patch.hasPos !== undefined) p.hasPos = patch.hasPos;
    p.lastSeen = Date.now();
    this.onPeerUpsert?.(p);
    if (isNew) this.emitStatus();
  }

  private dropPeer(id: string): void {
    if (this.peers.delete(id)) {
      this.onPeerLeft?.(id);
      this.emitStatus();
    }
  }

  private sweepStale(): void {
    const now = Date.now();
    for (const [id, p] of this.peers) {
      if (now - p.lastSeen > PEER_STALE_MS) {
        this.peers.delete(id);
        this.onPeerLeft?.(id);
      }
    }
    this.emitStatus();
  }

  private emitStatus(): void {
    this.onStatus?.(this.connected, this.peers.size);
  }
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
